import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { DocumentType, GeneralSettingsFields, PrinterDetailsInterface, SelectionType, SidebarSelection } from '../models/settings-printers.model';
import { Categories, Departments, Items, PaymentMethods, Printers, Rooms, Vat } from 'tilby-models';
import { ConfigurationManagerService, ConfigurationPreferences, documentTypes, EntityManagerService, ErrorsLoggerService, LoaderService } from 'src/app/core';
import { AlertDialogService } from 'src/app/dialogs';
import { $state } from 'app/ajs-upgraded-providers';
import { Transition } from 'angular-ui-router';
import { TranslateService } from '@ngx-translate/core';

const PRINTER_CATEGORIES_ORDER = ['FISCAL_PRINTER', 'NO_FISCAL_PRINTER', "NONFISCAL", "KDS", 'UNKNOWN'];

@Injectable()
export class PrintersModuleService {

  private readonly entityManagerService = inject(EntityManagerService);
  private readonly state = inject($state);
  private readonly destroyRef = inject(DestroyRef);
  private readonly configurationManagerService = inject(ConfigurationManagerService);
  private readonly translateService = inject(TranslateService);
  private readonly loaderService = inject(LoaderService);
  private readonly errorsLoggerService = inject(ErrorsLoggerService);
  private readonly alertDialogService = inject(AlertDialogService);

  private _selection = signal<SidebarSelection | null>(null);
  selection = this._selection.asReadonly();

  private _showDetailMobile = signal<boolean>(false);
  showDetailMobile = this._showDetailMobile.asReadonly();

  generalPreferencies: GeneralSettingsFields | null = null;

  printersList: Array<Printers> = [];
  printersMap: Map<string, Printers[]> = new Map();
  printersMapLoaded = signal(false);

  categories: Categories[] = [];
  vat: Vat[] = [];
  paymentMethods: PaymentMethods[] = [];
  rooms: Rooms[] = [];
  departments: Departments[] = [];
  
  isGlobalDataLoaded = signal(false);

  selectedPrinterId = signal<string | null>(null, {
    equal: (a, b) => a === b
  })

  printerDocumentTypes: DocumentType[] = []; 

  constructor() {
    this.syncSelectionWithRouter();

    const unsubscribeSuccess = this.state.router.transitionService.onSuccess(
      {}, (transition: Transition) => this.syncSelectionWithRouter(transition));
    

    this.destroyRef.onDestroy(() => {
      unsubscribeSuccess();
    });
  }

  updateSelection(type?: SelectionType, detail?: PrinterDetailsInterface) {
    if(!type) this._selection.set(null);
    else this._selection.set({ 
      type, 
      printer: detail?.printer, 
      options: detail?.options, 
      items: detail?.items, 
      generalSettings: detail?.generalSettings
    });
  }
  
  updatePrinterSelected(printer: Printers) {
    if(!printer) return;
    else this._selection.update(current => {
      return current ? { ...current, type: 'PRINTER', printer } : { type: 'PRINTER', printer } as any;
    });
  }

  updateShowDetailMobile(show: boolean) {
    this._showDetailMobile.set(show);
  }

  private syncSelectionWithRouter(transition?: Transition) {
    const stateName = transition ? transition.to().name : this.state.current.name;
    const params = transition ? transition.params() : this.state.params;

    if (stateName.endsWith('.general')) {
      this._selection.set({ type: 'GENERAL' });
    } 
    else if (stateName.endsWith('.detail') && params.id) {
      const newId = Number(params.id);
      const currentSelection = this._selection();

      if (currentSelection?.type !== 'PRINTER' || currentSelection.printer?.id !== newId) {
        const printer = this.findPrinterById(newId);
        
        this._selection.set({ 
          type: 'PRINTER', 
          printer: printer || ({ id: newId }) 
        });
      }
    }
  }

  private findPrinterById(id: number): Printers | undefined {
    for (const list of this.printersMap.values()) {
      const found = list.find(p => p.id === id);
      if (found) return found;
    }
    return undefined;
  }

  setGroupedPrinters(printers: Printers[]): void {
    this.printersMapLoaded.set(false);

    if(!printers.length) {
      this.printersList = [];
      this.printersMap = new Map();
      this.printersMapLoaded.set(true);
      return;
    }

    const groups = new Map<string, Printers[]>();

    PRINTER_CATEGORIES_ORDER.forEach(category => groups.set(category, []));

    printers.forEach((p: Printers, index: number) => {
      const category = this.getPrinterTypeForMap(p.type); 
      if (!groups.has(category)) { 
        groups.set(category, []);
      }
      groups.get(category)?.push(p);
    });

    for (const [key, value] of groups.entries()) {
      if (value.length === 0) {
        groups.delete(key);
      }
    }

    // console.log("groups ", groups)
    
    this.printersList = printers;
    this.printersMap = groups;
    this.printersMapLoaded.set(true);
  }

  private getPrinterTypeForMap(type: string): string {
    if(!type) return "UNKNOWN";
    switch (type) {
      case 'fiscal':
      case 'rt':
        return 'FISCAL_PRINTER';
      case 'nonfiscal':
        return 'NONFISCAL';
      case 'receipt':
        return 'NO_FISCAL_PRINTER';
      case 'kds':
        return 'KDS';
      default:
        return "UNKNOWN";
    }
  }

  async loadGlobalData() {
    if (this.isGlobalDataLoaded()) return;

    const [categories, vat, paymentMethods, rooms, departments] = await Promise.all([
      this.entityManagerService.categories.fetchCollectionOffline(),
      this.entityManagerService.vat.fetchCollectionOffline(),
      this.entityManagerService.paymentMethods.fetchCollectionOffline(),
      this.entityManagerService.rooms.fetchCollectionOffline(),
      this.entityManagerService.departments.fetchCollectionOffline()
    ]);

    this.categories = categories;
    this.vat = vat;
    this.paymentMethods = paymentMethods;
    this.rooms = rooms;
    this.departments = departments;

    this.isGlobalDataLoaded.set(true);
  }

  async loadPrinterList() {
    this.loaderService.show();
    
    try {
      const printers = await this.entityManagerService.printers.fetchCollectionOnline({ pagination: false });

      // this.loaderService.hide();
      // await new Promise(resolve => setTimeout(resolve, 500000))

      this.setGroupedPrinters(printers as Printers[] || []);

    } catch (error) {
      // without a message the list would just look empty
      this.errorsLoggerService.err(error, 'Failed to load the printers');
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant('PRINTERS.DETAIL.LOAD_LIST_ERROR')
        }
      });
    } finally {
      this.loaderService.hide();
    }
  }

  async getPrinterDetail(id: string | number): Promise<PrinterDetailsInterface | undefined> {
    const numericId = Number(id);
    if (isNaN(numericId)) return;

    try {
      const printer = await this.entityManagerService.printers.fetchOneOnline(id) as any;
      
      const options = this.calculateOptions(printer);
      
      let items: Partial<Items>[] = [];
      if (printer?.type === 'nonfiscal') {
        const allItems = await this.entityManagerService.items.fetchCollectionOffline();
        items = allItems
          .filter(item => item.printers?.some(p => p.id === printer.id))
          .map(item => ({ id: item.id, name: item.name, _enabled: true }));
      }

      return { printer, options, items };
    } catch(error) {
      // the caller shows the error when there is no detail
      this.errorsLoggerService.err(error, `Failed to load the printer ${id}`);
    }
  }

  private calculateOptions(printer: any) {
    let options: any = {};
    if (printer.type !== 'nonfiscal') {
      options.display_message = this.configurationManagerService.getPreference('fiscalprinter.display_message') || "";
      options.configure_header = this.configurationManagerService.getPreference('fiscalprinter.disable_header_configuration') !== true;
      for(let i = 1; i <= 13; i++) 
        options[`header${i}`] = this.configurationManagerService.getPreference(`fiscalprinter.printer_header_${i}`) || "";
      for(let i = 1; i <= 6; i++) 
        options[`invoice_footer${i}`] = this.configurationManagerService.getPreference(`fiscalprinter.printer_invoice_footer_${i}`) || "";
    }
    return options;
  }

  resetGeneralPreferencies() {
    this.generalPreferencies = null;
  }

  updateGeneralPreferences(newPrefs: GeneralSettingsFields) {
    this.generalPreferencies = newPrefs;
  }

  getGeneralPreferences() {
    if(this.generalPreferencies) return this.generalPreferencies;
    const fiscalprinter: any = {};
    const nofiscalprinter: any = {};

    const fiscalKeys = [
      'disable_header_configuration', 'ticket_change', 'print_notes',
      'print_description', 'print_name', 'print_barcode_id',
      'print_sale_qr_code', 'print_customer_details', 'print_seller',
      'print_daily_read_on_closing', 'display_message', 'seller_prefix',
      'tail', 'qr_code_message', 'qr_code_url'
    ];

    for (let i = 1; i <= 13; i++) fiscalKeys.push(`printer_header_${i}`);
    for (let i = 1; i <= 6; i++) fiscalKeys.push(`printer_invoice_footer_${i}`);

    const nofiscalKeys = ['goExit_skip_content', 'print_included_ingredients'];

    fiscalKeys.forEach(key => {
      fiscalprinter[key] = this.configurationManagerService.getPreference(`fiscalprinter.${key}` as keyof ConfigurationPreferences);
    });

    nofiscalKeys.forEach(key => {
      nofiscalprinter[key] = this.configurationManagerService.getPreference(`nofiscalprinter.${key}` as keyof ConfigurationPreferences);
    });

    this.generalPreferencies = { fiscalprinter, nofiscalprinter };
    return this.generalPreferencies;
  }

  getAvailableDocumentTypes(printerType: string) {
    if(!printerType || (printerType !== 'fiscal' && printerType !== 'rt' && printerType !== 'receipt')) {
      this.printerDocumentTypes = [];
      return;
    }

    this.printerDocumentTypes = documentTypes
      .filter((docType) => docType.printers.includes(printerType))
      .map((docType) => ({
        id: docType.id,
        check: docType.check || '',
        name: this.getDocumentTypeName(docType.id, printerType)
      }));
  }

  private getDocumentTypeName(id: string, printerType: string): string {
    const keys: Record<string, string> = {
      fiscal_receipt: printerType === 'rt' ? 'PRINTERS.DETAIL.COMMERCIAL_DOC' : 'PRINTERS.DETAIL.FISCAL_RECEIPT',
      receipt_invoice: 'PRINTERS.DETAIL.PRINT_RECEIPT_INVOICE',
      invoice: 'PRINTERS.DETAIL.PRINT_INVOICE',
      e_invoice: 'PRINTERS.DETAIL.PRINT_E_INVOICE',
      summary_e_nrc: 'PRINTERS.DETAIL.PRINT_SUMMARY_E_NRC',
      summary_e_rc: 'PRINTERS.DETAIL.PRINT_SUMMARY_E_RC',
      summary_invoice: 'PRINTERS.DETAIL.PRINT_SUMMARY_INVOICE',
      shipping_invoice: 'PRINTERS.DETAIL.PRINT_SHIPPING_INVOICE',
      receipt: 'PRINTERS.DETAIL.PRINT_RECEIPT',
      generic_receipt: 'PRINTERS.DETAIL.PRINT_RECEIPT',
      generic_invoice: 'PRINTERS.DETAIL.PRINT_GENERIC_INVOICE',
      generic_document: 'PRINTERS.DETAIL.PRINT_GENERIC_DOCUMENT',
    };

    const translationKey = keys[id] || '';
    return translationKey ? this.translateService.instant(translationKey) : id;
  }

  scrollToStart() {
    const sidebarContent = document.getElementById('sidebar-content');
    if(sidebarContent) {
      sidebarContent.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  }
}
